import React from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { surveyTheme as theme } from './surveyTheme';

/**
 * LikertScale - Horizontal scale question (e.g., 5-point agree/disagree)
 *
 * Props:
 *   question: { choices: [{id, text}] }
 *   selectedChoiceId: string
 *   onSelect: (choiceId) => void
 */
const LikertScale = ({ question, selectedChoiceId, onSelect, lang = 'en' }) => {
  const { choices = [] } = question;
  const loc = (en, es) => (lang === 'es' && es) ? es : en;

  return (
    <Box>
      {/* Scale buttons */}
      <Flex gap={2} justify="center">
        {choices.map((choice) => {
          const isSelected = selectedChoiceId === String(choice.id);

          return (
            <Box
              key={choice.id}
              as="button"
              type="button"
              onClick={() => onSelect(choice.id)}
              flex="1 1 0"
              minW="0"
              py={3}
              px={1}
              bg={isSelected ? theme.colors.teal : theme.colors.surface}
              color={isSelected ? 'white' : theme.colors.textSecondary}
              border="1.5px solid"
              borderColor={isSelected ? theme.colors.teal : theme.colors.borderLight}
              borderRadius="10px"
              cursor="pointer"
              transition={`all ${theme.transitions.fast}`}
              boxShadow={isSelected ? theme.shadows.selected : 'none'}
              _hover={{
                borderColor: theme.colors.teal,
                boxShadow: isSelected ? theme.shadows.selected : theme.shadows.subtle,
              }}
              _focus={{
                outline: 'none',
                boxShadow: `0 0 0 3px rgba(59, 107, 107, 0.3)`,
              }}
              _active={{ transform: 'scale(0.97)' }}
              textAlign="center"
              role="radio"
              aria-checked={isSelected}
            >
              <Text
                fontSize={{ base: '12px', md: '14px' }}
                fontWeight={isSelected ? 600 : 450}
                lineHeight="1.3"
                fontFamily={theme.typography.fontFamily}
              >
                {loc(choice.text, choice.text_es)}
              </Text>
            </Box>
          );
        })}
      </Flex>

      {/* Endpoint labels (first and last choice) */}
      {choices.length >= 3 && (
        <Flex justify="space-between" mt={2} px={1}>
          <Text
            fontSize="12px"
            color={theme.colors.textMuted}
            fontFamily={theme.typography.fontFamily}
          >
            {loc(choices[0].text, choices[0].text_es)}
          </Text>
          <Text
            fontSize="12px"
            color={theme.colors.textMuted}
            fontFamily={theme.typography.fontFamily}
          >
            {loc(choices[choices.length - 1].text, choices[choices.length - 1].text_es)}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default LikertScale;
